import { Button, Drawer, Space, Tabs, Tag, Tree, message } from 'antd';
import {
  getAdminPermissions,
  getMenuList,
  getRoleResources,
  updateAdminDirectPermissions,
} from 'apis/sys';
import LOCALS from 'commons/locals';
import i18n from 'i18n';
import _ from 'lodash';
import { ReactNode, useCallback, useEffect, useMemo, useState } from 'react';
import { Trans } from 'react-i18next';
import {
  AdminPermissionMenu,
  AdminPermissionResource,
  PermissionSource,
  SysRoleResourcesCategoryList,
} from 'types/sys';
import getMenuBuildTree, { SysMenuKey } from 'utils/getMenuBuildTree';

type MenuTreeNode = Omit<SysMenuKey, 'title' | 'children'> & {
  title: ReactNode;
  disableCheckbox?: boolean;
  children?: MenuTreeNode[];
};

type ResourceTreeNode = {
  key: string;
  name: ReactNode;
  disableCheckbox?: boolean;
  children?: ResourceTreeNode[];
};

interface Props {
  open: boolean;
  onClose: () => void;
  adminId?: number;
  adminName?: string;
}

const hasRoleSource = (sources?: PermissionSource[]) =>
  !!sources?.some((s) => s.type === 'role');

const renderSourceTags = (sources?: PermissionSource[]) => {
  if (!sources?.length) return null;
  return (
    <Space size={4} className="ml-2" wrap>
      {sources.map((source, index) => {
        if (source.type === 'role') {
          return (
            <Tag key={`role-${source.roleId}-${index}`} color="blue">
              {source.roleName || `role:${source.roleId}`}
            </Tag>
          );
        }
        return (
          <Tag key={`direct-${index}`} color="orange">
            <Trans i18nKey={LOCALS.direct_grant} />
          </Tag>
        );
      })}
    </Space>
  );
};

const AdminPermissionsDrawer = ({
  open,
  onClose,
  adminId,
  adminName,
}: Props) => {
  const [saving, setSaving] = useState(false);
  const [menuTree, setMenuTree] = useState<MenuTreeNode[]>([]);
  const [resourceTree, setResourceTree] = useState<ResourceTreeNode[]>([]);
  const [roleMenuIds, setRoleMenuIds] = useState<Set<number>>(new Set());
  const [roleResourceIds, setRoleResourceIds] = useState<Set<number>>(
    new Set(),
  );
  const [checkedMenuIds, setCheckedMenuIds] = useState<number[]>([]);
  const [checkedResourceKeys, setCheckedResourceKeys] = useState<string[]>([]);
  const [expandedMenuKeys, setExpandedMenuKeys] = useState<number[]>([]);
  const [expandedResourceKeys, setExpandedResourceKeys] = useState<string[]>(
    [],
  );
  const [resourceKeyById, setResourceKeyById] = useState<Map<number, string>>(
    new Map(),
  );
  const [resourceIdByKey, setResourceIdByKey] = useState<Map<string, number>>(
    new Map(),
  );

  const title = useMemo(() => {
    const base = i18n.t(LOCALS.permissions);
    return adminName ? `${base} - ${adminName}` : base;
  }, [adminName]);

  const menuRootIds = useMemo(() => menuTree.map((d) => d.id), [menuTree]);

  const roleMenuLeafIds = useMemo(
    () => Array.from(roleMenuIds).filter((id) => !menuRootIds.includes(id)),
    [menuRootIds, roleMenuIds],
  );

  const roleResourceKeys = useMemo(
    () =>
      Array.from(roleResourceIds)
        .map((id) => resourceKeyById.get(id))
        .filter((key): key is string => !!key),
    [resourceKeyById, roleResourceIds],
  );

  const buildMenuTreeWithSources = useCallback(
    (
      tree: SysMenuKey[],
      sourceMap: Map<number, PermissionSource[]>,
    ): MenuTreeNode[] =>
      tree.map((node) => {
        const sources = sourceMap.get(node.id);
        return {
          ...node,
          title: (
            <span>
              {node.title}
              {renderSourceTags(sources)}
            </span>
          ),
          disableCheckbox: hasRoleSource(sources),
          children: node.children
            ? buildMenuTreeWithSources(node.children, sourceMap)
            : undefined,
        };
      }),
    [],
  );

  const buildResourceTreeWithSources = (
    list: SysRoleResourcesCategoryList[],
    sourceMap: Map<number, PermissionSource[]>,
  ) => {
    const keyById = new Map<number, string>();
    const idByKey = new Map<string, number>();

    const tree: ResourceTreeNode[] = list.map((category) => {
      const children = category.children.map((resource) => {
        const key = `${category.id}-${resource.id}`;
        keyById.set(resource.id, key);
        idByKey.set(key, resource.id);
        const sources = sourceMap.get(resource.id);
        return {
          key,
          name: (
            <span>
              {resource.name}
              {renderSourceTags(sources)}
            </span>
          ),
          disableCheckbox: hasRoleSource(sources),
        };
      });

      return {
        key: `${category.id}`,
        name: category.name,
        children,
      };
    });

    return { tree, keyById, idByKey };
  };

  const loadData = useCallback(async () => {
    if (!open || !adminId) return;
    try {
      const [menus, resources, permissions] = await Promise.all([
        getMenuList(),
        getRoleResources(),
        getAdminPermissions(adminId),
      ]);

      const menuSourceMap = new Map<number, PermissionSource[]>();
      const nextRoleMenuIds = new Set<number>();
      permissions.menus.forEach((menu: AdminPermissionMenu) => {
        menuSourceMap.set(menu.id, menu.sources);
        if (hasRoleSource(menu.sources)) {
          nextRoleMenuIds.add(menu.id);
        }
      });

      const resourceSourceMap = new Map<number, PermissionSource[]>();
      const nextRoleResourceIds = new Set<number>();
      permissions.resources.forEach((resource: AdminPermissionResource) => {
        resourceSourceMap.set(resource.id, resource.sources);
        if (hasRoleSource(resource.sources)) {
          nextRoleResourceIds.add(resource.id);
        }
      });

      setRoleMenuIds(nextRoleMenuIds);
      setRoleResourceIds(nextRoleResourceIds);

      const tree = getMenuBuildTree(menus);
      setExpandedMenuKeys(tree.map((d) => d.id));
      setMenuTree(buildMenuTreeWithSources(tree, menuSourceMap));
      setCheckedMenuIds(
        _.difference(
          permissions.menus.map((m) => m.id),
          tree.map((d) => d.id),
        ),
      );

      const mappedResources = resources.map((d) => ({
        ...d,
        children: d.children.map((dd) => ({
          ...dd,
          key: `${d.id}-${dd.id}`,
        })),
        key: `${d.id}`,
      }));
      const {
        tree: nextResourceTree,
        keyById,
        idByKey,
      } = buildResourceTreeWithSources(mappedResources, resourceSourceMap);
      setResourceTree(nextResourceTree);
      setResourceKeyById(keyById);
      setResourceIdByKey(idByKey);
      setExpandedResourceKeys(nextResourceTree.map((d) => d.key));
      setCheckedResourceKeys(
        permissions.resources
          .map((r) => keyById.get(r.id))
          .filter((key): key is string => !!key),
      );
    } catch (err) {}
  }, [adminId, buildMenuTreeWithSources, open]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  const onSave = useCallback(async () => {
    if (!adminId) return;

    const menuIds: number[] = [...checkedMenuIds];
    menuTree.forEach((parent) => {
      parent.children?.forEach((child) => {
        if (checkedMenuIds.includes(child.id)) {
          menuIds.push(parent.id);
        }
      });
    });

    const directMenuIds = _.uniq(menuIds).filter((id) => !roleMenuIds.has(id));

    const directResourceIds = _.uniq(
      checkedResourceKeys
        .map((key) => resourceIdByKey.get(key))
        .filter((id): id is number => typeof id === 'number'),
    ).filter((id) => !roleResourceIds.has(id));

    setSaving(true);
    try {
      await updateAdminDirectPermissions({
        adminId,
        menuIds: directMenuIds,
        resourceIds: directResourceIds,
      });
      message.success(i18n.t('successful_operation'));
      onClose();
    } catch (err) {
    } finally {
      setSaving(false);
    }
  }, [
    adminId,
    checkedMenuIds,
    checkedResourceKeys,
    menuTree,
    onClose,
    resourceIdByKey,
    roleMenuIds,
    roleResourceIds,
  ]);

  return (
    <Drawer
      title={title}
      width="60%"
      open={open}
      onClose={onClose}
      destroyOnClose
      zIndex={1001}
      extra={
        <Button type="primary" loading={saving} onClick={onSave}>
          {i18n.t('save')}
        </Button>
      }
    >
      <Tabs
        items={[
          {
            key: 'menus',
            label: <Trans i18nKey={LOCALS.assign_menu} />,
            children: (
              <Tree
                checkable
                treeData={menuTree}
                fieldNames={{ title: 'title', key: 'id', children: 'children' }}
                checkedKeys={checkedMenuIds}
                onCheck={(keys) => {
                  const next = (Array.isArray(keys) ? keys : keys.checked).map(
                    Number,
                  );
                  setCheckedMenuIds(_.uniq([...next, ...roleMenuLeafIds]));
                }}
                expandedKeys={expandedMenuKeys}
                onExpand={(keys) => setExpandedMenuKeys(keys as number[])}
                showLine
              />
            ),
          },
          {
            key: 'resources',
            label: <Trans i18nKey={LOCALS.assign_resource} />,
            children: (
              <Tree
                checkable
                treeData={resourceTree}
                fieldNames={{
                  title: 'name',
                  key: 'key',
                  children: 'children',
                }}
                checkedKeys={checkedResourceKeys}
                onCheck={(keys) => {
                  const next = (
                    Array.isArray(keys) ? keys : keys.checked
                  ) as string[];
                  setCheckedResourceKeys(
                    _.uniq([...next, ...roleResourceKeys]),
                  );
                }}
                expandedKeys={expandedResourceKeys}
                onExpand={(keys) => setExpandedResourceKeys(keys as string[])}
                showLine
              />
            ),
          },
        ]}
      />
    </Drawer>
  );
};

export default AdminPermissionsDrawer;
